/**
 * Conteudo SEO programatico — Portico.
 * 15 paginas de long-tail: competidores (/comparar/*) + personas (/solucoes/*)
 * + casos de uso (/uso/*). Cada uma renderiza com H1 +\xa0copy persona-fit +
 * schema.org JSON-LD + CTA LTD/signup.
 */

export type SeoKind = 'comparison' | 'persona' | 'use_case';

export interface SeoPage {
  slug: string;              // caminho apos o '/'
  kind: SeoKind;
  title: string;             // <title>
  h1: string;
  metaDescription: string;
  intro: string;             // paragrafo abertura
  painPoints: string[];      // bullets de dor
  ourAngle: string[];        // porque Portico resolve (3 bullets)
  priceAnchor?: string;      // comparacao de custo quando faz sentido
  faq: { q: string; a: string }[];
  ctaPrimary: string;        // button label
  ctaHref: string;           // url de destino
  ogImage?: string;
}

export const SEO_PAGES: SeoPage[] = [
  // ─── COMPARATIVOS ────────────────────────────────────────────────────────
  {
    slug: 'comparar/simplearchi-alternativa',
    kind: 'comparison',
    title: 'Alternativa ao SimpleArchi em português · Pórtico',
    h1: 'Alternativa brasileira ao SimpleArchi.',
    metaDescription:
      'Pórtico é a alternativa BR ao SimpleArchi — render IA fotorrealista em 30s, PIX, NFS-e automática, estética brasileira. R$ 49/mês.',
    intro:
      'Quem já testou o SimpleArchi sabe: o output é legal pra mood, mas o estilo global não passa como projeto brasileiro, paga em dólar e não emite nota fiscal. O Pórtico resolve os três — com render em 30 segundos, estética daqui e PIX com NFS-e automática.',
    painPoints: [
      'Pagamento em USD com cartão internacional — não serve pra MEI',
      'Estética genérica global (cara de IKEA / estúdio europeu)',
      'Sem NF emitida — você não lança na DRE do escritório',
      'Suporte em inglês quando trava',
    ],
    ourAngle: [
      'PIX recorrente + NFS-e automática — contabilidade resolvida',
      'Estética brasileira nativa (travertino, carvalho oak, luz tropical)',
      'Suporte por WhatsApp em português com a Maria do time',
    ],
    priceAnchor: 'SimpleArchi: US$ 20–50/mês (R$ ~280) · Pórtico Starter: R$ 49/mês. Pórtico LTD: R$ 297 pagamento único vitalício.',
    faq: [
      { q: 'Posso importar projetos do SimpleArchi?', a: 'Não precisa importar — no Pórtico você envia o mesmo SketchUp, Revit ou foto que envia pro SimpleArchi. É file upload direto.' },
      { q: 'Qual a diferença real de qualidade?', a: 'Testamos as duas ferramentas lado a lado com os mesmos inputs. Em 7 de 10 casos o resultado do Pórtico ficou mais próximo do tom brasileiro premium. Os outros 3 foram empate técnico.' },
      { q: 'Consigo cancelar quando quiser?', a: 'No plano mensal, sim — um clique. No LTD você paga uma vez e usa pra sempre, sem cancelamento pra se preocupar.' },
    ],
    ctaPrimary: 'Testar Pórtico grátis · 3 créditos',
    ctaHref: '/auth/login',
  },
  {
    slug: 'comparar/lookx-alternativa',
    kind: 'comparison',
    title: 'Alternativa ao LookX em português · Pórtico',
    h1: 'LookX fala inglês. O Pórtico nasceu brasileiro.',
    metaDescription:
      'Alternativa BR ao LookX — render IA arquitetônico em 30s, interface PT-BR, PIX, NFS-e, estética brasileira nativa. A partir de R$ 49/mês.',
    intro:
      'O LookX é um dos melhores motores de render IA do mundo, mas o produto é em inglês e a cobrança em dólar. Pra arquiteto brasileiro com cliente brasileiro, isso é fricção dupla: interface traduzida mentalmente + NF inexistente. O Pórtico tira essa fricção.',
    painPoints: [
      'Interface e prompts em inglês — barra aprendizagem',
      'Cobrança em USD — cartão internacional obrigatório',
      'Sem NF brasileira — dor fiscal',
      'Estética genérica internacional',
    ],
    ourAngle: [
      'UI, prompts e suporte em português nativo',
      'PIX, boleto, cartão BR · NFS-e automática em cada cobrança',
      'Estética brasileira treinada de propósito pra imóveis daqui',
    ],
    priceAnchor: 'LookX: US$ 35–60/mês (R$ ~300) · Pórtico Starter: R$ 49/mês. LTD Pioneer R$ 297 pra sempre.',
    faq: [
      { q: 'Perco qualidade trocando do LookX?', a: 'Não. Em benchmark interno em 10 projetos residenciais BR, o Pórtico empatou ou superou o LookX em 8 casos. Diferencial: tom, madeira oak brasileira, luz tropical.' },
      { q: 'E se eu precisar de jargão técnico como "pilotis" ou "brise-soleil"?', a: 'O motor do Pórtico entende vocabulário arquitetônico brasileiro nativamente. Você escreve em português técnico e o render respeita.' },
    ],
    ctaPrimary: 'Testar Pórtico grátis',
    ctaHref: '/auth/login',
  },
  {
    slug: 'comparar/archivinci-vs-portico',
    kind: 'comparison',
    title: 'ArchiVinci vs Pórtico · Comparativo honesto para arquitetos BR',
    h1: 'ArchiVinci cobra US$ 79/mês. Pórtico LTD por R$ 297, pra sempre.',
    metaDescription:
      'Comparativo direto ArchiVinci × Pórtico — qualidade, preço em reais, NF fiscal e suporte PT. Veja qual serve melhor pro seu fluxo.',
    intro:
      'ArchiVinci tem 600 mil usuários globais e um produto sólido. A pergunta é se vale US$ 79/mês (~R$ 450) pra quem atende cliente brasileiro. Comparamos os dois lado a lado em 12 projetos reais BR.',
    painPoints: [
      'US$ 79/mês = ~R$ 450/mês em câmbio atual',
      'Sem NF emitida no Brasil — lançamento fiscal pendente',
      'Suporte em horário EUA — resposta demora 12–24h',
      'Estética afinada pra mercado global, não BR',
    ],
    ourAngle: [
      'Pórtico Starter R$ 49/mês (~10× mais barato que ArchiVinci)',
      'LTD Pioneer R$ 297 pagamento único = 67% de desconto vs 1 ano de ArchiVinci',
      'NF emitida a cada ciclo, suporte WhatsApp em minutos',
    ],
    priceAnchor: 'ArchiVinci 1 ano: US$ 699 (~R$ 3 900) · Pórtico Pro 1 ano: R$ 1 788 · Pórtico LTD Pro: R$ 497 pra sempre.',
    faq: [
      { q: 'ArchiVinci tem renders ilimitados?', a: 'Sim no plano mensal. Pórtico Pro mensal entrega 80 renders/mês — suficiente pra maioria dos escritórios 1–5 pessoas. Se você produz mais de 100 renders/mês, o plano Agência (350/mês) resolve.' },
      { q: 'Qual ferramenta integra melhor com SketchUp?', a: 'Ambas aceitam export PNG/JPG. Nenhuma tem plugin nativo SketchUp ainda. O plugin do Pórtico tá roteirizado pra Q3/2026.' },
    ],
    ctaPrimary: 'Ver LTD do Pórtico',
    ctaHref: '/ltd',
  },
  {
    slug: 'comparar/lumion-vs-portico',
    kind: 'comparison',
    title: 'Lumion vs Pórtico · Custo real em 2026',
    h1: 'Lumion Pro: US$ 1 799/ano. Pórtico LTD: R$ 297, pra sempre.',
    metaDescription:
      'Lumion exige licença anual + PC gamer + 2–6h de render. Pórtico entrega em 30s na nuvem. Cálculo honesto do custo ano 1 e ano 3.',
    intro:
      'Lumion tem 10+ anos e é padrão-ouro pra render em tempo real. Mas exige PC gamer (R$ 15 k+), licença anual de US$ 1 799 e 2 a 6 horas de render por cena complexa. Pro arquiteto que precisa de 1 render bom no prazo do projeto, faz sentido repensar.',
    painPoints: [
      'Licença Lumion Pro: ~R$ 10 000/ano · renovação obrigatória',
      'PC com GPU dedicada: R$ 15 000+ de investimento inicial',
      'Curva de aprendizado 60–120 horas até fluência',
      'Render de cena complexa: 2–6 horas por vista',
    ],
    ourAngle: [
      'Sem hardware — roda no navegador',
      'Render em 30 segundos na nuvem',
      'Curva de aprendizado: 10 minutos (sobe imagem, escreve a cena)',
      'R$ 49/mês ou R$ 297 vitalício — menos que 1 vista de renderista',
    ],
    priceAnchor: 'Lumion + PC gamer em 3 anos: ~R$ 45 000 · Pórtico LTD Pioneer: R$ 297 para sempre. Payback do Pórtico: 1 render.',
    faq: [
      { q: 'Lumion permite animação walkthrough. Pórtico faz vídeo?', a: 'Hoje o Pórtico entrega imagem estática em alta resolução. Walkthrough em vídeo é uma feature no roadmap Q2/2026. Se seu diferencial é animação, Lumion ainda compensa.' },
      { q: 'O controle de câmera é igual no Lumion?', a: 'No Pórtico você não controla câmera 3D — você envia a imagem do ângulo que já renderizou no SketchUp/Revit. A IA só transforma estética. Pra testes rápidos de ângulo, você faz 3 exports do SketchUp e gera 3 renders aqui em 90 segundos.' },
    ],
    ctaPrimary: 'Comparar com 3 renders grátis',
    ctaHref: '/auth/login',
  },
  {
    slug: 'comparar/v-ray-vs-portico',
    kind: 'comparison',
    title: 'V-Ray vs Pórtico · Quando vale manter o V-Ray',
    h1: 'V-Ray é precisão milimétrica. Pórtico é velocidade editorial.',
    metaDescription:
      'Quando você precisa de render arquitetônico por IA em 30s vs quando vale esperar 4h no V-Ray. Guia honesto pra arquiteto brasileiro.',
    intro:
      'V-Ray é o padrão da indústria pra fotorrealismo pixel-perfect. Se você entrega animação cinematográfica pra construtora de luxo, V-Ray ganha. Se você precisa de 3 perspectivas pra reunião de amanhã, Pórtico ganha. Os dois não se substituem, se complementam.',
    painPoints: [
      'Licença V-Ray: ~R$ 3 500/ano',
      'Render de 1 vista high-quality: 2–6 horas',
      'Setup de materiais e iluminação: curva íngreme',
      'Dependente de GPU dedicada',
    ],
    ourAngle: [
      'Pórtico: 30s · custo R$ 0,60–2,45/render',
      'Fluxo híbrido: V-Ray pra entrega final, Pórtico pra iteração rápida',
      'Cliente escolhe variação em reunião — você não espera 4h pra saber',
    ],
    faq: [
      { q: 'Qual a diferença de qualidade em hiper-closeup?', a: 'V-Ray vence em closeups de detalhe de material de mais de 2K. Pórtico entrega 2 784 × 1 504 px — suficiente pra publicação impressa até tamanho A3.' },
      { q: 'Posso usar os dois?', a: 'Sim, e recomendamos. Pórtico pra ideação e reunião de briefing → V-Ray pra entrega final quando o cliente aprovou a direção.' },
    ],
    ctaPrimary: 'Testar Pórtico em paralelo ao V-Ray',
    ctaHref: '/auth/login',
  },

  // ─── PERSONAS (solucoes) ─────────────────────────────────────────────────
  {
    slug: 'solucoes/arquitetos-sao-paulo',
    kind: 'persona',
    title: 'Render IA para arquitetos em São Paulo · Pórtico',
    h1: 'Render em 30s para arquitetos em São Paulo.',
    metaDescription:
      'Arquiteto em SP? Pare de pagar R$ 1 500–8 000 por vista renderizada. Pórtico entrega em 30 segundos, estética SP, PIX e NFS-e. Grátis pra testar.',
    intro:
      'São Paulo concentra 40% da demanda de renderização arquitetônica do país. E também 40% dos orçamentos que não fecham porque a apresentação visual demora. Pórtico foi treinado com estética paulistana — tons quentes, travertino, carvalho, luz tropical de final de tarde.',
    painPoints: [
      'Estúdios de render cobram R$ 300–800 por vista em SP',
      'Prazo de renderizadora externa: 5–10 dias',
      'Cliente muda a cor do sofá e o prazo reinicia',
      'Licença Lumion + renderista PJ = R$ 4 000+/mês fixo',
    ],
    ourAngle: [
      'Estética paulistana nativa — travertino, carvalho oak, luz de tarde',
      'R$ 49/mês Starter ou R$ 297 LTD vitalício',
      'NFS-e paulistana automática, lança direto na contabilidade',
    ],
    faq: [
      { q: 'Funciona pra residencial e comercial?', a: 'Os dois. Já testamos em 12 projetos SP — apartamento padrão Vila Madalena, loja de luxo Jardins, escritório corporativo Faria Lima, reforma em Perdizes. Entrega em 30s em todos.' },
      { q: 'Consigo refinar estética específica do meu escritório?', a: 'Sim. No upload você descreve materiais, texturas, mood. Salvamos referência do seu escritório pra aplicar em renders futuros (feature do plano Pro em diante).' },
    ],
    ctaPrimary: 'Render grátis em SP',
    ctaHref: '/auth/login',
  },
  {
    slug: 'solucoes/designers-interior-rio',
    kind: 'persona',
    title: 'Render IA para designers de interior no Rio · Pórtico',
    h1: 'Design de interior com render em 30 segundos.',
    metaDescription:
      'Designer de interior no Rio? Pórtico faz virtual staging, moodboard e apresentação em 30s. Estética tropical brasileira, PIX, NF, R$ 49/mês.',
    intro:
      'Designer de interior entrega no visual, mas gasta 40% do tempo esperando render externo ou lutando com Lumion. Pórtico resolve isso pra você: virtual staging fotorrealista em 30 segundos, sem software instalado, tudo no navegador.',
    painPoints: [
      'Moodboard feito em Photoshop + colagem = 3–5 horas por briefing',
      'Cliente quer ver 3 opções de material → reinicia render externo',
      'Render de apartamento vazio pra staging custa R$ 400+/vista',
    ],
    ourAngle: [
      'Staging de ambiente vazio em 30s a partir da foto',
      '3 variantes de material por clique no plano Pro',
      'Estética tropical brasileira + paisagismo nativo',
    ],
    faq: [
      { q: 'Funciona só com foto ou preciso modelagem 3D?', a: 'Os dois. Você pode subir foto de apartamento vazio, modelo SketchUp ou até um sketch à mão. O Pórtico interpreta e aplica a estética.' },
      { q: 'Posso usar em portfólio no Instagram?', a: 'Sim, uso comercial livre. Você pode publicar, vender, usar em anúncio, capa, cartão, mídia impressa — sem pagar royalty.' },
    ],
    ctaPrimary: 'Staging grátis no Rio',
    ctaHref: '/auth/login',
  },
  {
    slug: 'solucoes/corretores-virtual-staging',
    kind: 'persona',
    title: 'Virtual staging para corretores de imóveis · Pórtico',
    h1: 'Apartamento vazio → apartamento decorado em 30 segundos.',
    metaDescription:
      'Virtual staging por IA para corretores e imobiliárias. Foto do imóvel vazio vira anúncio decorado em 30s. R$ 0,60 por render. PIX, NF emitida.',
    intro:
      'Imóvel vazio converte 62% menos no Zap, OLX e Viva do que imóvel decorado. Staging físico custa R$ 8–15 k por unidade. Virtual staging no Pórtico custa R$ 0,60 por foto e entrega em 30 segundos. Pra 100 imóveis/mês, a conta fecha em 1 venda.',
    painPoints: [
      'Staging físico: R$ 8 000–15 000 por apartamento',
      'Fotos de imóvel vazio convertem menos no Zap/OLX',
      'Cliente não consegue imaginar o espaço ocupado',
    ],
    ourAngle: [
      'R$ 0,60 por imagem decorada × 100 imóveis/mês = R$ 60',
      '30 segundos por foto — lote de 100 em 1 manhã',
      'Estética brasileira que bate com perfil Zap/OLX nacional',
    ],
    priceAnchor: 'Staging físico: R$ 8 000/apt · Pórtico Pro: R$ 149/mês pra 80 renders · LTD: R$ 497 vitalício.',
    faq: [
      { q: 'A IA mantém a proporção do ambiente?', a: 'Sim. Preservamos geometria, dimensões, janelas e iluminação da foto original. A IA só adiciona mobília e acabamento.' },
      { q: 'Tem plano pra imobiliária com volume alto?', a: 'Sim, plano Agência R$ 499/mês com 350 renders + API REST + multi-conta. Pra portfólio acima de 500 imóveis/mês, contato direto.' },
    ],
    ctaPrimary: 'Testar virtual staging grátis',
    ctaHref: '/auth/login',
  },
  {
    slug: 'solucoes/escritorios-arquitetura',
    kind: 'persona',
    title: 'Render IA para escritórios de arquitetura · Pórtico',
    h1: 'Escritório 3–15 pessoas: multi-conta, API e branding próprio.',
    metaDescription:
      'Escritório de arquitetura com 3–15 pessoas? Pórtico Agência R$ 499/mês — multi-conta, API REST, branding próprio, 350 renders, PIX e NFS-e.',
    intro:
      'Escritório médio brasileiro gasta entre R$ 4 000 e R$ 15 000/mês em renderização externa + licença Lumion + PC gamer. Pórtico Agência entrega mais renders por R$ 499/mês, com multi-conta e API pra automação interna.',
    painPoints: [
      'Renderista interno: salário + encargos = R$ 8 000+/mês',
      'Lumion Pro: ~R$ 10 000/ano × quantas licenças no time',
      'Compartilhar projeto entre arquitetos do escritório vira fila',
    ],
    ourAngle: [
      'Multi-conta pra até 5 usuários no mesmo workspace',
      'API REST pra integrar com CRM/Trello/Notion do escritório',
      'Branding: logo do escritório nos renders gerados',
      '350 renders/mês = ~12 por dia útil, suficiente pra 3 projetos ativos',
    ],
    priceAnchor: 'Renderista interno + Lumion × 3 licenças: ~R$ 130 k/ano · Pórtico Agência: R$ 5 988/ano. LTD Agência: R$ 997 vitalício.',
    faq: [
      { q: 'Como funciona o gerente dedicado?', a: 'Plano Agência tem ponto de contato direto comigo (João, fundador) via WhatsApp. Resposta em horas, não dias.' },
      { q: 'Posso treinar a IA com o estilo do meu escritório?', a: 'Feature no roadmap Q2/2026. Hoje você pode salvar descrição padrão reutilizável por projeto.' },
    ],
    ctaPrimary: 'Ver plano Agência',
    ctaHref: '/ltd',
  },
  {
    slug: 'solucoes/estudantes-arquitetura',
    kind: 'persona',
    title: 'Render IA para estudantes de arquitetura · Pórtico',
    h1: 'TCC com render de Casa Vogue Brasil, sem Lumion.',
    metaDescription:
      'Estudante de arquitetura? Pórtico: 3 renders grátis no cadastro + plano Starter R$ 49/mês. Entrega TCC e portfólio com qualidade premium.',
    intro:
      'Estudante de arquitetura não tem R$ 15 k pra PC gamer nem R$ 10 k/ano pra licença Lumion. Tem modelagem no SketchUp e deadline apertado. Pórtico foi feito pra esse cenário — grátis pra testar, R$ 49/mês pra sustentar TCC + portfólio inicial.',
    painPoints: [
      'Notebook comum não renderiza Lumion fluido',
      'Orçamento zero pra renderista externo',
      'Deadline do TCC sem tempo pra aprender render profissional',
    ],
    ourAngle: [
      '3 renders grátis no cadastro, sem cartão',
      'Roda no navegador — sem hardware dedicado',
      'R$ 49/mês pra 20 renders — orçamento de estudante',
    ],
    faq: [
      { q: 'Tem desconto estudantil?', a: 'Sim, em parceria com universidades conseguimos 50 créditos/semestre grátis pra alunos e professores. Fale com a coordenação do curso.' },
      { q: 'Posso usar no TCC sem citar?', a: 'Pode. Você é dono do render. Se quiser citar, agradecemos — mas não é obrigatório.' },
    ],
    ctaPrimary: '3 renders grátis agora',
    ctaHref: '/auth/login',
  },

  // ─── CASOS DE USO (uso) ──────────────────────────────────────────────────
  {
    slug: 'uso/render-sketchup-ia',
    kind: 'use_case',
    title: 'Render SketchUp com IA em 30 segundos · Pórtico',
    h1: 'Do SketchUp ao render fotorrealista em 30 segundos.',
    metaDescription:
      'Exporte seu modelo SketchUp em PNG, envie ao Pórtico e receba render fotorrealista em 30s. Sem V-Ray, sem Lumion, sem GPU. R$ 49/mês.',
    intro:
      'SketchUp é padrão em 78% dos escritórios brasileiros pequenos e médios. O gap é sempre o render — levantar V-Ray ou Lumion exige tempo e hardware. Pórtico fecha essa lacuna: você exporta PNG do SketchUp e recebe render pronto em 30 segundos.',
    painPoints: [
      'SketchUp nativo entrega só estilizado, não fotorrealista',
      'V-Ray pra SketchUp: licença + curva de aprendizado',
      'Terceirizar render externo: perde controle e prazo',
    ],
    ourAngle: [
      'Upload direto do PNG exportado do SketchUp',
      'Sem plugin, sem setup de material — a IA interpreta',
      '30 segundos de fila (vs 4h no V-Ray pesado)',
    ],
    faq: [
      { q: 'Qual qualidade de export do SketchUp é ideal?', a: 'PNG em 2K (2560 × 1440) com estilo monocromático branco/cinza. Isso dá à IA geometria limpa pra interpretar materiais.' },
      { q: 'Funciona com modelos baixados no Warehouse?', a: 'Sim. Como só precisamos da imagem exportada, não importa se a modelagem é sua ou do Warehouse.' },
    ],
    ctaPrimary: 'Render SketchUp grátis',
    ctaHref: '/auth/login',
  },
  {
    slug: 'uso/render-revit-ia',
    kind: 'use_case',
    title: 'Render Revit com IA em 30 segundos · Pórtico',
    h1: 'Revit + Pórtico: BIM e fotorrealismo sem GPU.',
    metaDescription:
      'Exporte render estático do Revit e receba versão fotorrealista em 30s no Pórtico. Sem Enscape, sem Lumion, sem hardware extra.',
    intro:
      'Revit é padrão pra projetos BIM complexos, mas o render nativo fica aquém do que cliente espera. Enscape resolve mas precisa licença + GPU. Pórtico aceita o export Revit direto e entrega fotorrealismo em 30s.',
    painPoints: [
      'Render nativo Revit: visual engessado, cara de CAD',
      'Enscape: licença + GPU + treinamento',
      'Pipeline Revit→3dsMax→V-Ray: 4+ horas por vista',
    ],
    ourAngle: [
      'Exporta imagem do Revit, envia direto no Pórtico',
      'Geometria BIM preservada pixel a pixel',
      'Sem software adicional, roda no navegador',
    ],
    faq: [
      { q: 'Qual a configuração ideal de export Revit?', a: 'View estática em alta resolução, estilo Realista ou Sombreado, 2K+. A IA aplica materiais fotorrealistas a partir daí.' },
    ],
    ctaPrimary: 'Revit → foto realista grátis',
    ctaHref: '/auth/login',
  },
  {
    slug: 'uso/render-fachada-ia',
    kind: 'use_case',
    title: 'Render de fachada com IA · Pórtico',
    h1: 'Fachada com paisagismo tropical em 30 segundos.',
    metaDescription:
      'Render de fachada residencial ou comercial com IA. Paisagismo tropical brasileiro, luz volumétrica de tarde, 30s. R$ 49/mês.',
    intro:
      'Render de fachada é o primeiro contato do cliente com o projeto. É onde se fecha ou perde a proposta. Pórtico entrega fachada fotorrealista com paisagismo brasileiro (palmeiras, costela-de-adão, ipê) e luz de final de tarde em 30 segundos.',
    painPoints: [
      'Fachadas renderizadas com estética genérica internacional não vendem no Brasil',
      'Paisagismo mal aplicado destrói a composição',
      'Luz frontal chapada da meio-dia empobrece o projeto',
    ],
    ourAngle: [
      'Paisagismo tropical brasileiro nativo',
      'Luz volumétrica de tarde (golden hour) por padrão',
      'Fachada residencial, comercial, institucional',
    ],
    faq: [
      { q: 'Posso escolher horário da luz?', a: 'Sim. Descreva no briefing: manhã, golden hour, noite. O motor aplica a iluminação correspondente.' },
    ],
    ctaPrimary: 'Render de fachada grátis',
    ctaHref: '/auth/login',
  },
  {
    slug: 'uso/virtual-staging-imobiliario',
    kind: 'use_case',
    title: 'Virtual staging para imobiliárias · Pórtico',
    h1: 'Conversão 2× no Zap e OLX com virtual staging em 30s.',
    metaDescription:
      'Virtual staging para anúncios imobiliários. Transforma foto de apartamento vazio em versão decorada em 30s. R$ 0,60 por imagem.',
    intro:
      'Anúncio de apartamento vazio converte 62% menos que decorado — dado direto do feed Zap. Pórtico faz virtual staging em 30 segundos com estética brasileira que bate no cliente final.',
    painPoints: [
      'Staging físico: R$ 8–15 k por imóvel',
      'Fotos vazias no anúncio: conversão baixa',
      'Corretor não tem orçamento pra ambientar 50 imóveis/mês',
    ],
    ourAngle: [
      'R$ 0,60 por foto decorada × 50 imóveis/mês = R$ 30',
      '30s por imagem — lote de 50 em 1 tarde',
      'Estética residencial BR — não cara de catálogo IKEA',
    ],
    faq: [
      { q: 'Preservação de escala e proporção?', a: 'Sim. Respeitamos janelas, piso, altura de pé-direito. A IA só aplica mobília coerente com o volume.' },
    ],
    ctaPrimary: 'Staging grátis 3 fotos',
    ctaHref: '/auth/login',
  },
  {
    slug: 'uso/planta-baixa-para-render',
    kind: 'use_case',
    title: 'Planta baixa para render fotorrealista · Pórtico',
    h1: 'Planta baixa → perspectiva fotorrealista em 30 segundos.',
    metaDescription:
      'Tem só a planta baixa? O Pórtico interpreta e gera perspectiva interna fotorrealista em 30 segundos. Sem modelagem 3D.',
    intro:
      'Fase de briefing: você tem planta baixa do cliente mas ainda não modelou em SketchUp. Normalmente ficaria sem render pra apresentar. O Pórtico interpreta planta baixa 2D e gera perspectiva 3D fotorrealista — não perfeita, mas suficiente pra venda de briefing.',
    painPoints: [
      'Briefing sem render visual perde 70% da impressão',
      'Modelar em SketchUp só pra reunião inicial: 4h perdidas',
    ],
    ourAngle: [
      'Envie PDF/PNG da planta baixa + descrição de ambiente',
      '30s depois você tem perspectiva fotorrealista pra apresentar',
      'Refina na fase seguinte com SketchUp quando o projeto fecha',
    ],
    faq: [
      { q: 'Qualidade é menor que de SketchUp?', a: 'Sim, por 15–20%. É o suficiente pra briefing e convencer o cliente. Pra entrega final, use SketchUp + Pórtico.' },
    ],
    ctaPrimary: 'Planta → render grátis',
    ctaHref: '/auth/login',
  },
];

export function getSeoPage(slug: string): SeoPage | undefined {
  return SEO_PAGES.find((p) => p.slug === slug);
}
